// touchbase_frontend/src/features/scope_module/components/Toolbar.js
import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useScope } from '../../../contexts/DemoScopeContext';
import {
  Play, Square, Edit3, CheckCircle, Ban, LogOut,
  Layers, Sparkles, Film, Trash2
} from 'lucide-react';
import ConclusionDropdown from './ConclusionDropdown';

// Strategy (poster-only, LANDSCAPE PNG)
import {
  buildStrategyOverlays,
  buildStrategyPosterSvgLandscape,
  downloadStrategyPosterPng
} from '../utils/strategyPack';

function Toolbar() {
  const {
    // flags/state
    isTracking, isProcessing, hasTrackingCompleted, hasCompleted, analysisEnabled,
    toolState, conclusionOptions, selectedConclusion, sessionDetails,
    isSyntheticPreview, isImagePairPreview,
    // perms
    canStartTracking, canComplete,
    // actions
    startTracking, stopTrackingNow, handleToggleTool,
    handleSelectConclusion, handleCompleteSession, handleAnalyze,
    handleCancelSession,
    // data
    loadedImages, analysisResults, features, showToast, toolSessions
  } = useScope();

  const [isExporting, setIsExporting] = useState(false);
  const [highlightConclusion, setHighlightConclusion] = useState(false);
  const [highlightComplete, setHighlightComplete] = useState(false);
  const [hasEverTracked, setHasEverTracked] = useState(false);

  // --- REVIEW MODE: read-only UI if ?review=1 (or mode=review) ---
  const reviewMode = useMemo(() => {
    try {
      const qs = new URLSearchParams(window.location.search);
      if (qs.get('review') === '1' || qs.get('mode') === 'review') return true;
    } catch {}
    return !!sessionDetails?.review_mode;
  }, [sessionDetails]);

  // Track if user has ever started tracking in this session
  useEffect(() => {
    if (isTracking) {
      setHasEverTracked(true);
    }
  }, [isTracking]);

  // Only highlight conclusion dropdown when:
  // 1. User has actually tracked (started and stopped)
  // 2. Tracking is complete
  // 3. Session is not finalized
  // 4. No conclusion selected yet
  useEffect(() => {
    if (!reviewMode && hasEverTracked && hasTrackingCompleted && !hasCompleted && !selectedConclusion) {
      setHighlightConclusion(true);
      setHighlightComplete(false);
    } else if (selectedConclusion) {
      setHighlightConclusion(false);
    }
  }, [reviewMode, hasEverTracked, hasTrackingCompleted, hasCompleted, selectedConclusion]);

  useEffect(() => { if (hasCompleted) setHighlightComplete(false); }, [hasCompleted]);

  const jobId = sessionDetails?.image_pair?.comparison_job_id;

  // Button state classes
  const getStartButtonClass = () => {
    if (reviewMode) return 'toolbar-btn';
    if (isTracking) return 'toolbar-btn danger';
    if (canStartTracking) return 'toolbar-btn success';
    return 'toolbar-btn';
  };

  const getToolButtonClass = (isActive) => {
    if (reviewMode) return 'toolbar-btn';
    return isActive ? 'toolbar-btn active' : 'toolbar-btn';
  };

  const handleStartStop = useCallback(() => {
    if (reviewMode || isProcessing) return;
    if (isTracking) stopTrackingNow(); else startTracking();
  }, [reviewMode, isProcessing, isTracking, stopTrackingNow, startTracking]);

  // ---------- Layer Info Export ----------
  const saveCanvasPNG = (canvas, filename) => {
    try {
      const url = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL?.(url);
    } catch (e) {
      console.error(e);
    }
  };

  const drawImageFitted = (ctx, img, dx, dy, dw, dh) => {
    if (!img) return { rx: dx, ry: dy, rw: dw, rh: dh, scale: 1 };
    const iw = img.width, ih = img.height;
    const scale = Math.min(dw / iw, dh / ih);
    const rw = Math.round(iw * scale), rh = Math.round(ih * scale);
    const rx = Math.round(dx + (dw - rw) / 2), ry = Math.round(dy + (dh - rh) / 2);
    ctx.drawImage(img, rx, ry, rw, rh);
    return { rx, ry, rw, rh, scale };
  };

  const exportLayerInfo = useCallback(async () => {
    if (!loadedImages?.enhanced) { showToast('Base image not loaded.', 2000, 'error'); return; }

    setIsExporting(true);
    try {
      const styles = getComputedStyle(document.documentElement);
      const bgColor = styles.getPropertyValue('--background-secondary')?.trim() || '#22252a';
      const textPrimary = styles.getPropertyValue('--text-primary')?.trim() || '#e0e0e0';
      const textSecondary = styles.getPropertyValue('--text-secondary')?.trim() || '#b0b0b0';
      const borderColor = styles.getPropertyValue('--border-color')?.trim() || '#383c4a';

      const sid = sessionDetails?.id ?? '';

      // PAGE 1: 10x8 landscape
      const P1 = document.createElement('canvas'); P1.width = 3000; P1.height = 2400;
      const p1 = P1.getContext('2d');
      p1.fillStyle = bgColor; p1.fillRect(0,0,3000,2400);
      p1.fillStyle = textPrimary; p1.font = 'bold 42px system-ui'; p1.fillText('Layer Composite',80,70);
      p1.font = '28px system-ui'; p1.fillStyle = textSecondary; p1.fillText(`Session: ${sid}`,80,110);
      const areaX=80, areaY=130, areaW=3000-160, areaH=2400-210;
      const { rx, ry, scale } = drawImageFitted(p1, loadedImages?.enhanced, areaX, areaY, areaW, areaH);
      if (loadedImages?.heatmap) { p1.globalAlpha = 1; drawImageFitted(p1, loadedImages.heatmap, areaX, areaY, areaW, areaH); }
      if (loadedImages?.clarity) { p1.globalAlpha = 0.2; drawImageFitted(p1, loadedImages.clarity, areaX, areaY, areaW, areaH); }
      if (loadedImages?.quality) { p1.globalAlpha = 1; drawImageFitted(p1, loadedImages.quality, areaX, areaY, areaW, areaH); }

      if (!loadedImages?.quality && features && features.length > 0) {
        p1.globalAlpha = 1;
        p1.fillStyle = '#00ff00';
        p1.strokeStyle = '#00ff00';
        p1.lineWidth = 2 * scale;
        for (const f of features) {
          const x = rx + f.x * scale;
          const y = ry + f.y * scale;
          const radius = 3 * scale;
          p1.beginPath();
          p1.arc(x, y, radius, 0, Math.PI * 2);
          p1.fill();
          if (typeof f.angleDeg === 'number') {
            const L = 18 * scale;
            const rad = (f.angleDeg * Math.PI) / 180;
            p1.beginPath();
            p1.moveTo(x, y);
            p1.lineTo(x + Math.cos(rad) * L, y + Math.sin(rad) * L);
            p1.stroke();
          }
        }
      }
      p1.globalAlpha = 1; p1.strokeStyle = borderColor; p1.lineWidth=2; p1.strokeRect(areaX-1, areaY-1, areaW+2, areaH+2);
      saveCanvasPNG(P1, `layer-info-page1-10x8-session-${sid||'unknown'}.png`);

      // PAGE 2: 8x10 portrait
      const P2 = document.createElement('canvas'); P2.width = 2400; P2.height = 3000;
      const p2 = P2.getContext('2d');
      p2.fillStyle = bgColor; p2.fillRect(0,0,P2.width,P2.height);
      p2.fillStyle = textPrimary; p2.font = 'bold 42px system-ui'; p2.fillText('Minutiae & Path',80,70);
      p2.font = '28px system-ui'; p2.fillStyle = textSecondary; p2.fillText(`Session: ${sid}`,80,110);
      const PAD2=80; const halfH = Math.floor((P2.height - (PAD2 * 2) - 60) / 2);
      const topX=PAD2, topY=140, topW=P2.width - PAD2*2, topH=halfH;
      const botX=PAD2, botY=topY + halfH + PAD2, botW=topW, botH=halfH;

      const { rx: topRx, ry: topRy, scale: topScale } = drawImageFitted(p2, loadedImages?.enhanced, topX, topY, topW, topH);
      if (features && features.length > 0) {
        p2.globalAlpha = 1;
        p2.fillStyle = '#00ff00';
        p2.strokeStyle = '#00ff00';
        p2.lineWidth = 2 * topScale;
        for (const f of features) {
          const x = topRx + f.x * topScale;
          const y = topRy + f.y * topScale;
          p2.beginPath();
          p2.arc(x, y, 3 * topScale, 0, Math.PI * 2);
          p2.fill();
          if (typeof f.angleDeg === 'number') {
            const L = 18 * topScale;
            const rad = (f.angleDeg * Math.PI) / 180;
            p2.beginPath();
            p2.moveTo(x, y);
            p2.lineTo(x + Math.cos(rad) * L, y + Math.sin(rad) * L);
            p2.stroke();
          }
        }
      }
      p2.strokeStyle = borderColor; p2.lineWidth=2; p2.strokeRect(topX-1, topY-1, topW+2, topH+2);
      p2.fillStyle = textSecondary; p2.font = '600 28px system-ui'; p2.fillText('Top: Base + Minutiae (All)', topX, topY - 12);

      const { rx: botRx, ry: botRy, scale: botScale } = drawImageFitted(p2, loadedImages?.enhanced, botX, botY, botW, botH);
      if (features && features.length > 0) {
        p2.globalAlpha = 1;
        p2.fillStyle = '#00ff00';
        p2.strokeStyle = '#00ff00';
        p2.lineWidth = 2 * botScale;
        for (const f of features) {
          const x = botRx + f.x * botScale;
          const y = botRy + f.y * botScale;
          p2.beginPath();
          p2.arc(x, y, 3 * botScale, 0, Math.PI * 2);
          p2.fill();
          if (typeof f.angleDeg === 'number') {
            const L = 18 * botScale;
            const rad = (f.angleDeg * Math.PI) / 180;
            p2.beginPath();
            p2.moveTo(x, y);
            p2.lineTo(x + Math.cos(rad) * L, y + Math.sin(rad) * L);
            p2.stroke();
          }
        }
      }

      if (toolSessions && toolSessions.length > 0) {
        p2.globalAlpha = 1;
        p2.strokeStyle = '#00ffff';
        p2.lineWidth = 2 * botScale;
        p2.lineCap = 'round';
        p2.lineJoin = 'round';
        toolSessions.forEach(session => {
          const pathPoints = session.pathPoints || [];
          if (pathPoints.length < 2) return;
          p2.beginPath();
          p2.moveTo(botRx + pathPoints[0].x * botScale, botRy + pathPoints[0].y * botScale);
          for (let i = 1; i < pathPoints.length; i++) {
            p2.lineTo(botRx + pathPoints[i].x * botScale, botRy + pathPoints[i].y * botScale);
          }
          p2.stroke();
        });
      }

      p2.strokeStyle = borderColor; p2.lineWidth=2; p2.strokeRect(botX-1, botY-1, botW+2, botH+2);
      p2.fillStyle = textSecondary; p2.font = '600 28px system-ui'; p2.fillText('Bottom: Base + Minutiae + Tracking (Add)', botX, botY - 12);
      saveCanvasPNG(P2, `layer-info-page2-8x10-session-${sid||'unknown'}.png`);

      showToast('Layer Info pages saved.', 2200, 'success');
    } catch (e) {
      console.error(e);
      showToast('Failed to create Layer Info pages.', 3000, 'error');
    } finally {
      setIsExporting(false);
    }
  }, [loadedImages, sessionDetails, showToast, features, toolSessions]);

  // ---------- STRATEGY: poster-only (LANDSCAPE PNG) ----------
  const handleStrategy = useCallback(async () => {
    const base = loadedImages?.enhanced;
    if (!base) { showToast('Base image not loaded.', 2000, 'error'); return; }
    const W = base.width, H = base.height;

    const allTrimmed = [];
    if (Array.isArray(toolSessions)) {
      toolSessions.forEach(s => {
        const evs = Array.isArray(s?.events) ? s.events.slice() : [];
        if (!evs.length) return;
        evs.sort((a,b)=>a.t-b.t);
        const t0 = evs[0].t, t1 = evs[evs.length-1].t;
        const pts = Array.isArray(s.pathPoints) ? s.pathPoints.filter(p => p.t >= t0 && p.t <= t1) : [];
        if (pts.length) allTrimmed.push(...pts);
      });
    }
    allTrimmed.sort((a,b) => (a.t||0)-(b.t||0));

    const trimmedEvents = [];
    if (Array.isArray(toolSessions)) {
      toolSessions.forEach(s => {
        const evs = Array.isArray(s?.events) ? s.events.slice() : [];
        if (!evs.length) return;
        evs.sort((a,b)=>a.t-b.t);
        const t0 = evs[0].t, t1 = evs[evs.length-1].t;
        evs.forEach(ev => {
          if (typeof ev?.t !== 'number') return;
          if (ev.t < t0 || ev.t > t1) return;
          trimmedEvents.push({
            op: ev.type === 'delete' ? 'delete' : 'add',
            t: ev.t,
            x: typeof ev.x === 'number' ? ev.x : undefined,
            y: typeof ev.y === 'number' ? ev.y : undefined,
            quality: Number.isFinite(ev.quality) ? ev.quality : undefined
          });
        });
      });
    }

    try {
      setIsExporting(true);
      const overlays = buildStrategyOverlays({
        imageWidth: W, imageHeight: H,
        trimmedPath: allTrimmed, trimmedFeatures: trimmedEvents,
        grid: { cols: 10, rows: 8 },
        baseImage: loadedImages?.enhanced || null,
        clarityImage: loadedImages?.clarity || loadedImages?.quality || null,
        qualityImage: loadedImages?.quality || null
      });

      const sid = sessionDetails?.id || 'current';
      const header = 'Strategy Summary';
      const subHeader = `Session ${sid}`;

      const posterSvg = buildStrategyPosterSvgLandscape({
        overlays, posterWidth: 3000, posterHeight: 2400, header, subHeader
      });

      await downloadStrategyPosterPng(posterSvg, `strategy-session-${sid}`);
      showToast('Strategy poster downloaded (Landscape PNG).', 2200, 'success');
    } catch (e) {
      console.error(e);
      showToast('Failed to build Strategy poster.', 2500, 'error');
    } finally {
      setIsExporting(false);
    }
  }, [loadedImages, toolSessions, sessionDetails, showToast]);

  // Disabled logic
  const startDisabled = reviewMode || isProcessing || (!isTracking && !canStartTracking);
  const toolToggleDisabled = reviewMode || isProcessing || !isTracking;
  const conclusionDisabled = reviewMode || isProcessing || !hasTrackingCompleted || hasCompleted;
  const completeDisabled = reviewMode || isProcessing || !hasTrackingCompleted || !selectedConclusion || hasCompleted;
  const analyzeDisabled = reviewMode || isProcessing || !hasCompleted || analysisEnabled;
  const postAnalyzeDisabled = isProcessing || isExporting;
  const cancelDisabled = reviewMode || isProcessing || hasCompleted;

  const onChangeConclusion = (e) => {
    const v = e.target.value;
    if (reviewMode) return;
    handleSelectConclusion(v);
    if (v) { setHighlightConclusion(false); setHighlightComplete(true); }
  };

  const onClickComplete = async () => {
    if (reviewMode) return;
    await handleCompleteSession();
    setHighlightComplete(false);
  };

  const onClickExit = useCallback(() => {
    // Just navigate away - session data is already saved
    if (reviewMode) {
      window.location.assign('/sessions/completed');
    } else {
      window.location.assign('/');
    }
  }, [reviewMode]);

  // Synthetic preview mode: show minimal toolbar with just exit button
  if (isSyntheticPreview) {
    return (
      <div className="toolbar toolbar-synthetic-preview">
        <div className="toolbar-group">
          <div className="toolbar-synthetic-label">
            <Layers size={14} />
            <span>Synthetic Pair Preview</span>
          </div>
        </div>
        <div className="toolbar-group">
          <button
            className="toolbar-btn danger"
            onClick={() => window.history.back()}
            title="Return to Synthetic Pairs"
          >
            <LogOut size={14}/> Exit Preview
          </button>
        </div>
      </div>
    );
  }

  // Image pair preview mode: show minimal toolbar with just exit button
  if (isImagePairPreview) {
    return (
      <div className="toolbar toolbar-image-pair-preview">
        <div className="toolbar-group">
          <div className="toolbar-image-pair-label">
            <Layers size={14} />
            <span>Image Pair Preview</span>
          </div>
        </div>
        <div className="toolbar-group">
          <button
            className="toolbar-btn danger"
            onClick={() => window.history.back()}
            title="Return to Image Pairs"
          >
            <LogOut size={14}/> Exit Preview
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="toolbar">
      {/* Group 1: Tracking Controls */}
      <div className="toolbar-group">
        <button
          className={getStartButtonClass()}
          onClick={handleStartStop}
          disabled={startDisabled}
          title={reviewMode ? 'Read-only review' : (isTracking ? 'End Tracking' : 'Start Tracking')}
        >
          {isTracking ? <Square size={14}/> : <Play size={14}/>}
          {isTracking ? 'Stop' : 'Start'}
        </button>

        <div className="toolbar-separator" />

        <button
          className={getToolButtonClass(toolState?.current !== 'delete')}
          onClick={() => { if (!reviewMode && toolState?.current === 'delete') handleToggleTool(); }}
          disabled={toolToggleDisabled}
          title="Add minutiae tool"
        >
          <Edit3 size={14}/> Add
        </button>

        <button
          className={getToolButtonClass(toolState?.current === 'delete')}
          onClick={() => { if (!reviewMode && toolState?.current !== 'delete') handleToggleTool(); }}
          disabled={toolToggleDisabled}
          title="Delete minutiae tool"
        >
          <Trash2 size={14}/> Delete
        </button>
      </div>

      {/* Group 2: Conclusion & Complete */}
      <div className="toolbar-group">
        <ConclusionDropdown
          options={conclusionOptions || []}
          value={selectedConclusion}
          onChange={onChangeConclusion}
          disabled={conclusionDisabled}
          highlight={!reviewMode && highlightConclusion}
        />

        <button
          className={`toolbar-btn ${hasTrackingCompleted && !hasCompleted && !reviewMode ? 'active' : ''} ${(!reviewMode && highlightComplete) ? 'pulse-highlight' : ''}`}
          onClick={onClickComplete}
          disabled={completeDisabled}
          title={reviewMode ? 'Read-only review' : 'Complete the session'}
        >
          <CheckCircle size={14}/> Complete
        </button>
      </div>

      {/* Group 3: Analysis & Export */}
      <div className="toolbar-group">
        <button
          className={`toolbar-btn ${hasCompleted && !analysisEnabled && !reviewMode ? 'active' : ''}`}
          onClick={handleAnalyze}
          disabled={analyzeDisabled}
          title={reviewMode ? 'Disabled in review mode' : 'Run analysis'}
        >
          <Film size={14}/> Analyze
        </button>

        <button
          className="toolbar-btn warning"
          onClick={handleStrategy}
          disabled={postAnalyzeDisabled}
          title="Download strategy poster"
        >
          <Sparkles size={14}/> Strategy
        </button>

        <button
          className="toolbar-btn warning"
          onClick={exportLayerInfo}
          disabled={postAnalyzeDisabled}
          title="Export Layer Info"
        >
          <Layers size={14}/> Layers
        </button>
      </div>

      {/* Group 4: Session Controls */}
      <div className="toolbar-group">
        <button
          className="toolbar-btn"
          onClick={onClickExit}
          disabled={isProcessing}
          title="Exit to home"
        >
          <LogOut size={14}/> Exit
        </button>

        <button
          className="toolbar-btn"
          onClick={() => !reviewMode && handleCancelSession(jobId)}
          disabled={cancelDisabled}
          title={reviewMode ? 'Disabled in review mode' : 'Cancel this session'}
        >
          <Ban size={14}/> Cancel
        </button>
      </div>
    </div>
  );
}

export default Toolbar;
